"use client"

import { addToCartEventBus } from "@/lib/data/cart-event-bus"
import { getProductPrice } from "@/lib/util/get-product-price"
import { aud, variantTradeInfo } from "@/lib/util/trade"
import { sortVariants, variantLabel } from "@/lib/util/variants"
import { backorderUnits, stockClass, variantStock } from "@/lib/util/stock"
import Button from "@/modules/common/components/button"
import ShoppingBag from "@/modules/common/icons/shopping-bag"
import { HttpTypes, StoreProduct, StoreProductVariant } from "@medusajs/types"
import { useState } from "react"
import BulkTableQuantity from "../bulk-table-quantity"

/** Trade order table: one row per size with trade price, RRP, margin and quantity. Stacks on mobile. */
const ProductVariantsTable = ({ product, region, cartonExtra = 0 }: { product: HttpTypes.StoreProduct; region: HttpTypes.StoreRegion; cartonExtra?: number }) => {
  const [isAdding, setIsAdding] = useState(false)
  const [lines, setLines] = useState<Map<string, StoreProductVariant & { product: StoreProduct; quantity: number }>>(new Map())
  const [added, setAdded] = useState(false)
  const variants = sortVariants(product.variants ?? [])

  const totalQuantity = Array.from(lines.values()).reduce((acc, l) => acc + l.quantity, 0)
  const boTotal = Array.from(lines.values()).reduce((acc, l) => acc + backorderUnits(l, l.quantity), 0)
  const totalValue = Array.from(lines.values()).reduce((acc, l) => acc + (l.quantity - backorderUnits(l, l.quantity)) * (((l as any).calculated_price?.calculated_amount as number) ?? 0), 0)

  const setQty = (variantId: string, quantity: number) => {
    setAdded(false)
    setLines((prev) => {
      const next = new Map(prev)
      const base = prev.get(variantId) ?? { ...variants.find((v) => v.id === variantId)!, product, quantity: 0 }
      next.set(variantId, { ...base, quantity })
      return next
    })
  }

  const handleAddToCart = async () => {
    setIsAdding(true)
    const lineItems = Array.from(lines.values())
      .filter((l) => l.quantity > 0)
      .map(({ quantity, ...variant }) => ({ productVariant: { ...variant }, quantity }))
    addToCartEventBus.emitCartAdd({ lineItems, regionId: region.id })
    setIsAdding(false)
    setAdded(true)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-image border border-elevate-border overflow-hidden bg-white">
        <div className="hidden small:grid grid-cols-[1fr_1.2fr_1fr_150px] gap-3 px-4 py-2 bg-elevate-grey-light text-xs uppercase tracking-[0.06em] text-elevate-grey-mid">
          <span>Size</span>
          <span>SKU · availability</span>
          <span>Trade (ex GST)</span>
          <span className="text-right pr-2">Quantity</span>
        </div>
        <ul className="divide-y divide-elevate-border">
          {variants.map((variant: any) => {
            const { variantPrice } = getProductPrice({ product, variantId: variant.id })
            const i = variantTradeInfo(variant)
            const stock = variantStock(variant)
            return (
              <li key={variant.id} className="grid grid-cols-[1fr_auto] small:grid-cols-[1fr_1.2fr_1fr_150px] items-center gap-x-3 gap-y-1 px-4 py-3">
                <span className="flex flex-col leading-tight">
                  <span className="font-medium">{variantLabel(variant)}</span>
                  {stock.label && <span className={`small:hidden text-xs ${stockClass(stock.tone)}`}>{stock.label}</span>}
                </span>
                <span className="hidden small:flex flex-col text-xs break-all">
                  <span className="text-elevate-grey-mid">{variant.sku}{(variant as any).metadata?.carton_qty ? ` · carton of ${(variant as any).metadata.carton_qty}` : ""}</span>
                  {stock.label && <span className={stockClass(stock.tone)}>{stock.label}</span>}
                </span>
                <span className="flex flex-col leading-tight row-start-2 small:row-start-auto">
                  <span className="font-medium">{variantPrice?.calculated_price ?? "—"}</span>
                  <span className="text-sm text-elevate-grey-mid">
                    {i.rrp !== null ? `RRP ${aud(i.rrp)}` : "No RRP"}
                    {i.margin !== null ? ` · ${Math.round(i.margin * 100)}% margin` : ""}
                  </span>
                </span>
                <div className="row-span-2 small:row-span-1 col-start-2 small:col-start-auto w-[150px]">
                  <BulkTableQuantity variantId={variant.id} onChange={setQty} label={`Quantity for ${variantLabel(variant)}`} carton={Number((variant as any).metadata?.carton_qty) || undefined} cartonSave={cartonExtra} />
                </div>
              </li>
            )
          })}
        </ul>
      </div>
      <Button
        onClick={handleAddToCart}
        variant="primary"
        className="w-full h-12 !rounded-none uppercase tracking-[0.1em] text-xs"
        isLoading={isAdding}
        disabled={totalQuantity === 0}
        data-testid="add-product-button"
      >
        <ShoppingBag className="text-white" fill={totalQuantity === 0 ? "none" : "#fff"} />
        {totalQuantity === 0 ? "Set quantities to add" : `Add ${totalQuantity} to order · ${aud(totalValue)} ex GST`}
      </Button>
      {boTotal > 0 && <p className="text-sm text-elevate-gold-dark">{boTotal} on backorder: not charged now. We&apos;ll email you when {boTotal === 1 ? "it's" : "they're"} in stock.</p>}
      {added && <p role="status" className="text-sm text-emerald-800">Added to your order.</p>}
    </div>
  )
}

export default ProductVariantsTable
